import type { Dispatch, SetStateAction } from "react";
import {
  certifyMaterialUsage,
  extractMaterialUsage,
  getMaterialUsageDraft,
  reopenMaterialUsage,
  saveMaterialUsageCorrection,
  saveMaterialUsageDraft,
  startMaterialUsageCorrection,
  submitMaterialUsage,
  type MaterialUsageLine,
  type MaterialUsagePreview,
  type MaterialUsageReportStatus,
} from "@/lib/service/materialUsageClient";

type Commands = {
  preview: MaterialUsagePreview | null;
  draftId: string | null;
  correctsReportId: string | null;
  serialText: string;
  transcript: string | null;
  canWrite: boolean;
  reloadDrafts: () => Promise<void>;
  setPreview: Dispatch<SetStateAction<MaterialUsagePreview | null>>;
  setSerialText: Dispatch<SetStateAction<string>>;
  setTranscript: Dispatch<SetStateAction<string | null>>;
  setDraftId: Dispatch<SetStateAction<string | null>>;
  setReportStatus: Dispatch<SetStateAction<MaterialUsageReportStatus | null>>;
  setCorrectsReportId: Dispatch<SetStateAction<string | null>>;
  setMailStatus: Dispatch<SetStateAction<string | null>>;
  setHasFile: Dispatch<SetStateAction<boolean>>;
  setBusy: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setNotice: Dispatch<SetStateAction<string | null>>;
};

function correctionLines(preview: MaterialUsagePreview) {
  return preview.lines.flatMap((line) => (line.id ? [{ id: line.id, quantity: line.quantity }] : []));
}

export function useMaterialUsageCommands(input: Commands) {
  async function openDraft(id: string) {
    input.setBusy(true);
    input.setError(null);
    input.setNotice(null);
    input.setMailStatus(null);
    input.setHasFile(false);
    const loaded = await getMaterialUsageDraft(id);
    if (!loaded.ok) {
      input.setBusy(false);
      input.setError(loaded.error);
      return;
    }
    const report = loaded.data.report;
    const status = report.status === "certified" || report.status === "submitted" ? report.status : "draft";
    if (!input.canWrite || report.status === "submitted" || report.corrects_report_id) {
      input.setBusy(false);
      input.setDraftId(report.id);
      input.setReportStatus(status);
      input.setCorrectsReportId(report.corrects_report_id);
      input.setMailStatus(report.mail_status || null);
      input.setHasFile(report.has_file);
      input.setTranscript(report.transcript);
      input.setSerialText(report.serial_text);
      input.setPreview({
        serial_text: report.serial_text,
        mfg_unit_id: report.mfg_unit_id,
        serial_found: Boolean(report.mfg_unit_id),
        plant_id: report.plant_id,
        plant_name: null,
        plant_status: report.plant_id ? "matched" : "unmatched",
        plant_candidates: [],
        plants: report.plant_id ? [{ id: report.plant_id, name: "Plant" }] : [],
        catalog: report.catalog,
        lines: report.lines.map((line) => ({
          id: line.id,
          spoken_phrase: line.spoken_phrase,
          quantity: line.quantity,
          match_status: line.match_status,
          material_id: line.material_id,
          material_name: line.material_name,
          unit: line.unit,
          candidates:
            line.material_id && line.material_name
              ? [{ id: line.material_id, name: line.material_name, unit: line.unit ?? "" }]
              : [],
        })),
      });
      return;
    }
    const matched = await extractMaterialUsage({
      plant_id: report.plant_id ?? undefined,
      serial_text: report.serial_text,
      lines: report.lines.map((line) => ({ quantity: line.quantity, spoken_phrase: line.spoken_phrase })),
    });
    input.setBusy(false);
    if (!matched.ok) {
      input.setError(matched.error);
      return;
    }
    const lines = matched.data.preview.lines.map((line, index) => {
      const saved = report.lines[index];
      if (!saved?.material_id) return { ...line, id: saved?.id };
      const material = matched.data.preview.catalog.find((row) => row.id === saved.material_id);
      return {
        ...line,
        id: saved.id,
        material_id: saved.material_id,
        material_name: material?.name ?? line.material_name,
        unit: material?.unit ?? line.unit,
        match_status: saved.match_status === "matched" ? "matched" : "overridden",
      } as MaterialUsageLine;
    });
    input.setDraftId(report.id);
    input.setCorrectsReportId(null);
    input.setMailStatus(report.mail_status || null);
    input.setHasFile(report.has_file);
    input.setReportStatus(status);
    input.setTranscript(report.transcript);
    input.setSerialText(report.serial_text);
    input.setPreview({ ...matched.data.preview, serial_text: report.serial_text, lines });
  }

  async function onSave() {
    if (!input.preview) return;
    if (input.correctsReportId && input.draftId) {
      input.setBusy(true);
      input.setError(null);
      const result = await saveMaterialUsageCorrection({ id: input.draftId, lines: correctionLines(input.preview) });
      input.setBusy(false);
      if (!result.ok) {
        input.setError(result.error);
        return;
      }
      input.setNotice("Correction saved.");
      await input.reloadDrafts();
      return;
    }
    input.setBusy(true);
    input.setError(null);
    const result = await saveMaterialUsageDraft({
      id: input.draftId ?? undefined,
      plant_id: input.preview.plant_id,
      serial_text: input.serialText,
      transcript: input.transcript,
      lines: input.preview.lines.map((line) => ({
        spoken_phrase: line.spoken_phrase,
        quantity: line.quantity,
        material_id: line.material_id,
        match_status: line.match_status,
      })),
    });
    input.setBusy(false);
    if (!result.ok) {
      input.setError(result.error);
      return;
    }
    input.setDraftId(result.data.id);
    input.setReportStatus("draft");
    input.setNotice("Draft saved.");
    await input.reloadDrafts();
  }

  async function onCertify() {
    if (!input.preview) return;
    if (input.correctsReportId && input.draftId) {
      input.setBusy(true);
      input.setError(null);
      const saved = await saveMaterialUsageCorrection({ id: input.draftId, lines: correctionLines(input.preview) });
      if (!saved.ok) {
        input.setBusy(false);
        input.setError(saved.error);
        return;
      }
      const certified = await certifyMaterialUsage(input.draftId);
      input.setBusy(false);
      if (!certified.ok) {
        input.setError(certified.error);
        return;
      }
      input.setReportStatus("certified");
      input.setNotice(`Certified by ${certified.data.certified_name}.`);
      await input.reloadDrafts();
      return;
    }
    input.setBusy(true);
    input.setError(null);
    const saved = await saveMaterialUsageDraft({
      id: input.draftId ?? undefined,
      plant_id: input.preview.plant_id,
      serial_text: input.serialText,
      transcript: input.transcript,
      lines: input.preview.lines.map((line) => ({
        spoken_phrase: line.spoken_phrase,
        quantity: line.quantity,
        material_id: line.material_id,
        match_status: line.match_status,
      })),
    });
    if (!saved.ok) {
      input.setBusy(false);
      input.setError(saved.error);
      return;
    }
    const certified = await certifyMaterialUsage(saved.data.id);
    input.setBusy(false);
    if (!certified.ok) {
      input.setDraftId(saved.data.id);
      input.setReportStatus("draft");
      input.setError(certified.error);
      return;
    }
    input.setDraftId(saved.data.id);
    input.setReportStatus("certified");
    input.setNotice(`Certified by ${certified.data.certified_name}.`);
    await input.reloadDrafts();
  }

  async function onReopen() {
    if (!input.draftId) return;
    input.setBusy(true);
    input.setError(null);
    const result = await reopenMaterialUsage(input.draftId);
    input.setBusy(false);
    if (!result.ok) {
      input.setError(result.error);
      return;
    }
    input.setReportStatus("draft");
    input.setNotice("Reopened. Certify again after the edit.");
    await input.reloadDrafts();
  }

  async function onSubmit() {
    if (!input.draftId) return;
    input.setBusy(true);
    input.setError(null);
    const result = await submitMaterialUsage(input.draftId);
    input.setBusy(false);
    if (!result.ok) {
      input.setError(result.error);
      return;
    }
    input.setReportStatus("submitted");
    input.setMailStatus(result.data.mail_status);
    input.setNotice("Submitted. Stock is drawn.");
    await input.reloadDrafts();
  }

  async function onCorrect() {
    if (!input.draftId) return;
    input.setBusy(true);
    input.setError(null);
    const started = await startMaterialUsageCorrection(input.draftId);
    input.setBusy(false);
    if (!started.ok) {
      input.setError(started.error);
      return;
    }
    await openDraft(started.data.id);
  }

  return { openDraft, onSave, onCertify, onReopen, onSubmit, onCorrect };
}
